import { Box, HStack, IconButton, Text } from '@chakra-ui/react';
import { useState } from 'react';
import { FiColumns, FiLayers, FiX } from 'react-icons/fi';

import { usePageContentSidebar } from '#/providers/page-layout-provider';

type ContentSidebarLayoutMode = 'float' | 'fixed';

const SIDEBAR_WIDTH = { base: 'min(100%, 360px)', md: '360px' } as const;

/** Float overlay: full width below `md`, capped at 500px on larger viewports. */
const FLOAT_SIDEBAR_WIDTH = { base: '100%', md: 'min(100%, 500px)' } as const;

/**
 * Right-hand column or overlay for document chat (portal target). Default is float over content.
 */
export function PageContentSidebar() {
  const {
    contentSidebarRef,
    contentSidebarPanelVisible,
    requestContentSidebarClose,
  } = usePageContentSidebar();
  const [layoutMode, setLayoutMode] =
    useState<ContentSidebarLayoutMode>('float');

  if (!contentSidebarPanelVisible) {
    return null;
  }

  const shellProps = {
    minW: 0,
    minH: 0,
    display: 'flex',
    flexDirection: 'column' as const,
    borderLeftWidth: 'var(--border-base-width)',
    borderColor: 'var(--border-base-color)',
    bg: 'var(--background-base-color)',
  };

  const header = (
    <HStack
      flexShrink={0}
      px={2}
      py={2}
      gap={1}
      align="center"
      justify="space-between"
      borderBottomWidth="1px"
      borderColor="var(--border-base-color)"
      bg="var(--background-base-color)"
    >
      <Text fontSize="sm" fontWeight="semibold" color="fg" truncate>
        Document AI
      </Text>
      <HStack gap={0}>
        <IconButton
          type="button"
          size="xs"
          variant="ghost"
          aria-label={
            layoutMode === 'float'
              ? 'Dock sidebar (push content)'
              : 'Float sidebar over content'
          }
          title={
            layoutMode === 'float'
              ? 'Dock sidebar (fixed column)'
              : 'Float sidebar over content'
          }
          onClick={() =>
            setLayoutMode((m) => (m === 'float' ? 'fixed' : 'float'))
          }
        >
          {layoutMode === 'float' ? (
            <FiColumns size={16} aria-hidden />
          ) : (
            <FiLayers size={16} aria-hidden />
          )}
        </IconButton>
        <IconButton
          type="button"
          size="xs"
          variant="ghost"
          aria-label="Close AI sidebar"
          title="Close AI sidebar"
          onClick={requestContentSidebarClose}
        >
          <FiX size={18} aria-hidden />
        </IconButton>
      </HStack>
    </HStack>
  );

  const body = (
    <Box
      ref={contentSidebarRef}
      flex={1}
      minH={0}
      width="100%"
      px={3}
      py={3}
      overflowY="auto"
    />
  );

  if (layoutMode === 'fixed') {
    return (
      <Box flexShrink={0} w={SIDEBAR_WIDTH} {...shellProps} zIndex={1}>
        {header}
        {body}
      </Box>
    );
  }

  return (
    <Box
      position="absolute"
      top={0}
      right={0}
      bottom={0}
      w={FLOAT_SIDEBAR_WIDTH}
      zIndex={20}
      shadow="lg"
      {...shellProps}
    >
      {header}
      {body}
    </Box>
  );
}
